import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  Dialog,
  Autocomplete,
} from "@mui/material";
import toast from "react-hot-toast";
import axios from "axios";
import { URL } from "../config";

const formatDateForInput = (date) =>
  date ? new Date(date).toISOString().split("T")[0] : "";
const today = new Date().toISOString().split("T")[0];

const AddPackagePurchaseModal = ({ onClose, data, refresh }) => {
  const isEdit = Boolean(data);
  const [loading, setLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [packageSubTypes, setPackageSubTypes] = useState([]);

  const [formData, setFormData] = useState({
    fkcustomerId: "",
    fkpackageId: "",
    noOfCamera: "",
    totalPrice: "",
    packagePrice: "",
    packageStartDate: "",
    packageEndDate: "",
  });

 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, pkgRes, typeRes, subTypeRes] = await Promise.all([
          axios.get(`${URL}/customers`),
          axios.get(`${URL}/packages`),
          axios.get(`${URL}/packagetypes`),
          axios.get(`${URL}/packagesubtypes`),
        ]);
        setCustomers(custRes.data);
        setPackages(pkgRes.data);
        setPackageTypes(typeRes.data);
        setPackageSubTypes(subTypeRes.data);
      } catch (err) {
        toast.error("Failed to fetch data");
        console.error(err);
      }
    };
    fetchData();
  }, []);


useEffect(() => {
  if (data) {
    const selectedPackage = packages.find(p => p.packageId === data.fkpackageId);
    const packagePrice = selectedPackage?.packagePrice || 0;
    const totalPrice = (Number(data.noOfCamera) || 0) * packagePrice;

    setFormData({
      fkcustomerId: data.fkcustomerId || "",
      fkpackageId: data.fkpackageId || "",
      noOfCamera: data.noOfCamera || "",
      packagePrice: packagePrice,
      totalPrice: totalPrice,
      packageStartDate: formatDateForInput(data.packageStartDate),
      packageEndDate: formatDateForInput(data.packageEndDate),
    });
  }
}, [data, packages]);


  const handleChange = (key, value) => {
    const updated = { ...formData, [key]: value };

    if (key === "noOfCamera" || key === "fkpackageId") {
      const selectedPackage = packages.find(
        (p) => p.packageId === (key === "fkpackageId" ? value : formData.fkpackageId)
      );
      const price = selectedPackage?.packagePrice || 0;
      const cameras =
        key === "noOfCamera" ? Number(value) : Number(updated.noOfCamera || 0);
      updated.packagePrice = price;
      updated.totalPrice = cameras * price;
    }

    setFormData(updated);
  };


  useEffect(() => {
    if (
      formData.packageStartDate &&
      formData.packageEndDate &&
      formData.packageEndDate < formData.packageStartDate
    ) {
      handleChange("packageEndDate", "");
    }
  }, [formData.packageStartDate]);


  const handleSave = async () => {
    if (!formData.fkcustomerId || !formData.fkpackageId) {
      toast.error("Customer & Package are required");
      return;
    }

    setLoading(true);

    try {

      const payload = new FormData();
      payload.append("fkcustomerId", formData.fkcustomerId);
      payload.append("fkpackageId", formData.fkpackageId);
      payload.append("noOfCamera", formData.noOfCamera);
      payload.append("totalPrice", formData.totalPrice);
      payload.append("packageStartDate", formData.packageStartDate);
      payload.append("packageEndDate", formData.packageEndDate);

      if (isEdit) {
        await axios.patch(
          `${URL}/packagepurchasecustomers/${data.purchaseId}`,
          payload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Package updated successfully");
      } else {
        await axios.post(`${URL}/packagepurchasecustomers`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Package purchased successfully");
      }

      refresh?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save package");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": {
        borderColor: "#c4c4c4",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px",
      color: "#184459ff",
      fontWeight: 600,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#184459ff" },
  };

  const getPackageDisplay = (pkg) => {
    const type =
      packageTypes.find((t) => t.packageTypeId === pkg.fkpackageTypeId)
        ?.packageTypeName || "-";
    const subType =
      packageSubTypes.find((st) => st.packageSubTypeId === pkg.fkpackageSubTypeId)
        ?.packageSubTypeName || "-";
    return `${type} - ${subType} - ${pkg.packageTitle}`;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { width: { xs: "90%", sm: 500 }, borderRadius: 2, maxHeight: "90vh" },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.4)" } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" textAlign="center" sx={{ color: "#184459ff" }}>
            {isEdit ? "Edit Package Purchase" : "Add Package Purchase"}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
          <Stack spacing={2}>
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => option.CustomerName || ""}
              value={customers.find((c) => c.CustomerId === formData.fkcustomerId) || null}
              onChange={(e, value) => handleChange("fkcustomerId", value ? value.CustomerId : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={fieldStyle}
                />
              )}
              ListboxProps={{ sx: { maxHeight: 250, overflow: "auto" } }}
            />

            <Autocomplete
              options={packages}
              getOptionLabel={(option) => getPackageDisplay(option)}
              value={packages.find((p) => p.packageId === formData.fkpackageId) || null}
              onChange={(e, value) => handleChange("fkpackageId", value ? value.packageId : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Package *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={fieldStyle}
                />
              )}
              ListboxProps={{ sx: { maxHeight: 250, overflow: "auto" } }}
            />

            <TextField
              label="No of Cameras"
              size="small"
              fullWidth
              value={formData.noOfCamera}
              onChange={(e) => handleChange("noOfCamera", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

            <TextField
  label="Per Camera Price"
  size="small"
  fullWidth
  value={formData.packagePrice}
  InputProps={{ readOnly: true }}
  InputLabelProps={{ shrink: true }}   
  sx={fieldStyle}
/>

<TextField
  label="Total Price"
  size="small"
  fullWidth
  value={formData.totalPrice}
  InputProps={{ readOnly: true }}
  InputLabelProps={{ shrink: true }} 
  sx={fieldStyle}
/>


            <TextField
              type="date"
              label="Package Start Date"
              size="small"
              fullWidth
              value={formData.packageStartDate}
              onChange={(e) => handleChange("packageStartDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
              sx={fieldStyle}
            />

            <TextField
              type="date"
              label="Package End Date"
              size="small"
              fullWidth
              value={formData.packageEndDate}
              onChange={(e) => handleChange("packageEndDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: formData.packageStartDate || today }}
              sx={fieldStyle}
            />
          </Stack>
        </Box>

        <Box sx={{ p: 3, borderTop: "1px solid #c4c4c4" }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#184459ff" }}
              onClick={handleSave}
              startIcon={loading && <CircularProgress size={18} />}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Save"}
            </Button>
            <Button variant="contained" sx={{ backgroundColor: "#757575ff" }} onClick={onClose}>
              Close
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddPackagePurchaseModal;
